import { DOCUMENT } from '@angular/common';
import { inject, Injectable, TransferState } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import qs from 'qs';
import { Observable, of, Subject } from 'rxjs';
import { map, skip, take } from 'rxjs/operators';
import { parseQueryParams } from 'src/app/util';
import { BackStagePayload } from 'src/app/web-generator/template-generator/entity/back-stage-payload';
import { REQUEST } from '../../../express.tokens';
import { CURRENT_LANGS_TOKEN } from '../tokens/current-langs.token';
import { IS_BROWSER_TOKEN } from '../tokens/is-browser.token';
import { IS_SERVER_TOKEN } from '../tokens/is-server.token';
import { IS_SSR_MODE_TOKEN } from '../tokens/is-ssr-mode.token';
import { SITE_CONFIG_KEY } from '../tokens/site-config-key.token';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class ServerConfigService {
  protected request = inject(REQUEST, { optional: true }) ?? null;
  private readonly tState = inject(TransferState, { optional: true }) ?? null;
  private readonly translate = inject(TranslateService);
  private readonly loggerService = inject(LoggerService);
  private readonly document = inject(DOCUMENT);
  /** 是否為客戶端渲染 */
  isBrowser = inject(IS_BROWSER_TOKEN);
  /** 是否為NODE端渲染 */
  isServer = inject(IS_SERVER_TOKEN);
  /** 是否為 SSR 模式 */
  isSSRMode = inject(IS_SSR_MODE_TOKEN);
  /** 目前有的語系列表 */
  currentLangs = inject(CURRENT_LANGS_TOKEN);
  /** 頁面請求時的網址(不根據CSR頁面轉導後更換)，ex:http(s)://hostName:port/baseHref/zh-tw/participate/75?stage=aHR0cCUzQSUyRiUyRmxvY2FsaG9zdCUzQTQzMDA%3D */
  url: string;
  /** 頁面請求時的路徑(不根據CSR頁面轉導後更換)，ex:/baseHref/zh-tw/participate/75 */
  pathname: string;
  /** 頁面請求時的查詢參數(不根據CSR頁面轉導後更換)，ex: {stage: aHR0cCUzQSUyRiUyRmxvY2FsaG9zdCUzQTQzMDA%3D} */
  queryParams: { [key: string]: string };
  stringifyQueryParams: string;
  /** 是否為編輯模式的 url */
  isEditModeUrl: boolean;
  /** 網站 basehref */
  baseHref: string = '/';
  config: {
    /** 語系 */
    lang: string;
    /** 從後台塞過來的資料格式 */
    backStagePayload?: BackStagePayload;
  };
  /** http://hostName:port */
  origin: string;

  constructor() {
    if (!this.isBrowser) {
      this.loggerService.info(`isBrowser: ${this.isBrowser}; isServer: ${this.isServer}; isSSRMode: ${this.isSSRMode}`);
    }

    const baseHref = this.document.head.getElementsByTagName('base')[0].getAttribute('href');
    this.baseHref = this.pruneTailingSlash(baseHref);

    if (this.isBrowser) {
      this.url = location.href;
      this.pathname = location.pathname;
      const queryParam = location.search.slice(1, location.search.length);
      this.queryParams = queryParam ? parseQueryParams(queryParam) : {};
      this.stringifyQueryParams = queryParam;
      this.origin = location.origin;
    } else {
      this.url = `${this.request.protocol}://${this.request.get('host')}${this.request.originalUrl}`;
      this.loggerService.info(`url: ${this.url}`);
      this.pathname = this.request.path;
      this.loggerService.info(`pathname: ${this.pathname}`);
      const queryParam = qs.stringify(this.request.query);
      this.stringifyQueryParams = queryParam;
      this.queryParams = queryParam ? parseQueryParams(queryParam) : {};
      this.loggerService.info(`queryParams: `, this.queryParams);
      this.origin = `${this.request.protocol}://${this.request.get('host')}`;
    }
    this.isEditModeUrl = !!this.queryParams['stage'] || this.stringifyQueryParams.indexOf('stage') !== -1;
    if (!this.isBrowser) {
      this.loggerService.info(`isEditModeUrl: ${this.isEditModeUrl}`);
    }
  }

  private pruneTailingSlash(baseHref: string) {
    if (baseHref.endsWith('/')) {
      return baseHref.slice(0, - 1);
    }
    return baseHref;
  }

  getConfig(backStageSubj?: Subject<BackStagePayload>): Observable<{ lang: string; backStagePayload?: BackStagePayload; }> {
    if (this.tState.hasKey(SITE_CONFIG_KEY)) {
      // 瀏覽器 SSR 開啟
      this.config = JSON.parse(this.tState.get(SITE_CONFIG_KEY, null as any));
      window['printAllState'] = this.tState.toJson();
    } else if (this.isServer) {
      // NODE SSR 開啟
      const urlSegArr = this.pathname.split('/');
      const lang = urlSegArr[1].endsWith('.html') ? urlSegArr[1].replace('.html', '') : urlSegArr[1];
      this.config = { lang };
      this.tState.set(SITE_CONFIG_KEY, JSON.stringify(this.config));
    } else {
      // 瀏覽器 CSR 開啟
      const urlSegArr = this.pathname.split('/');
      const lang = urlSegArr[1].endsWith('.html') ? urlSegArr[1].replace('.html', '') : urlSegArr[1];
      this.config = { lang };
    }

    if (this.config.lang && this.config.lang !== '404') {
      // 設定i18n
      this.translate.use(this.config.lang);
    }

    if (backStageSubj) {
      // 從後台開的
      backStageSubj.pipe(
        skip(1),
      ).subscribe(v => {
        this.config.backStagePayload = v;
      });

      return backStageSubj.pipe(
        take(1),
        map(backStagePayload => {
          this.config.backStagePayload = backStagePayload;
          return this.config;
        }),
      );
    } else {
      // 直接進前台的
      return of(this.config);
    }
  }

  getCurrentUrl() {
    return this.isServer ? this.url : location.href;
  }

  getCurrentPathname() {
    return this.isServer ? this.pathname : location.pathname;
  }

  getCurrentQueryParams() {
    if (this.isServer) {
      return this.queryParams;
    }
    const queryParamStr = location.search.slice(1, location.search.length);
    const queryParams = queryParamStr ? parseQueryParams(queryParamStr) : {};
    return queryParams;
  }
}
